import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Play, Star, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const posterUrl = (p) => (p?.startsWith("http") ? p : p ? `${BACKEND_URL}/api/files/${p}` : "");
const FALLBACK = "https://images.unsplash.com/photo-1572188863110-46d457c9234d?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

const priceLabel = (c) => {
  if (c.is_free || !c.pricing) return "FREE";
  const p = c.pricing;
  const tiers = ["ppv", "rent_day", "rent_week", "rent_month", "rent_year", "subscription"];
  const min = tiers.map(t => p[t]?.enabled ? Number(p[t].price) : null).filter(v => v != null);
  return min.length ? `FROM ₹${Math.min(...min).toLocaleString("en-IN")}` : "FREE";
};

const Poster = ({ c }) => (
  <Link to={`/app/watch/${c.id}`} data-testid={`content-card-${c.id}`}
    className="group relative shrink-0 w-[190px] rounded-lg overflow-hidden border border-white/10 hover:border-kinetic/60 transition-all duration-300 bg-[#121212]">
    <div className="aspect-[2/3] overflow-hidden bg-black">
      <img src={posterUrl(c.poster) || FALLBACK} alt={c.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={(e) => { e.currentTarget.src = FALLBACK; }} />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-90" />
    <div className="absolute bottom-0 left-0 right-0 p-3">
      <div className="font-sans font-bold text-sm leading-tight line-clamp-2">{c.title}</div>
      <div className="mono text-white/50 mt-1 truncate">{c.channel}</div>
      <div className="flex items-center justify-between mt-1.5">
        <span className={`mono ${c.is_free ? "text-kinetic" : "text-white/70"} inline-flex items-center gap-1`}>
          {c.is_free ? <Play className="w-3 h-3" /> : <Lock className="w-3 h-3" />} {priceLabel(c)}
        </span>
        {c.rating_avg > 0 && <span className="mono text-white/60 inline-flex items-center gap-1"><Star className="w-3 h-3 fill-kinetic text-kinetic" />{c.rating_avg}</span>}
      </div>
    </div>
  </Link>
);

export default function WatchDashboard() {
  const [items, setItems] = useState([]);
  const [genres, setGenres] = useState([]);
  const [channels, setChannels] = useState([]);
  const [topSelling, setTopSelling] = useState([]);
  const [trending, setTrending] = useState([]);
  const [artists, setArtists] = useState([]);
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [feed, ch, top, trend, arts] = await Promise.all([
        api.watchFeed(q, genre), api.watchChannels(), api.topSelling(), api.watchTrending(), api.watchArtists(),
      ]);
      setItems(feed.items || []);
      setGenres(feed.genres || []);
      setChannels(ch.channels || []);
      setTopSelling(top.items || []);
      setTrending(trend.items || []);
      setArtists(arts.artists || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [q, genre]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const featured = items[0];

  return (
    <div className="space-y-10" data-testid="watch-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="mono text-white/40">VIEWERS DASHBOARD · ARTIST TO AUDIENCE</div>
          <h1 className="text-4xl font-extrabold tracking-tight mt-2">Now Streaming</h1>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input data-testid="watch-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search films, shows, channels…" className="bg-black border-white/15 rounded-sm pl-9" />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button data-testid="genre-all" onClick={() => setGenre("")}
          className={`mono px-3 h-8 rounded-sm border ${genre === "" ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>ALL</button>
        {genres.map(g => (
          <button key={g} data-testid={`genre-${g}`} onClick={() => setGenre(g)}
            className={`mono px-3 h-8 rounded-sm border ${genre === g ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>{g.toUpperCase()}</button>
        ))}
      </div>

      {loading && <div className="mono text-white/40 py-16 text-center">LOADING…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-dashed border-white/15 rounded-lg p-16 text-center mono text-white/40" data-testid="watch-empty">
          NO CONTENT YET · STREAMERS CAN PUBLISH FROM THE STUDIO
        </div>
      )}

      {featured && (
        <Link to={`/app/watch/${featured.id}`} data-testid="watch-featured"
          className="group relative block rounded-xl overflow-hidden border border-white/10 h-[46vh] min-h-[320px]">
          <img src={posterUrl(featured.poster) || FALLBACK} alt={featured.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            onError={(e) => { e.currentTarget.src = FALLBACK; }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-end p-8 max-w-2xl">
            <div className="mono text-kinetic">{featured.genre?.toUpperCase() || "FEATURED"} · {featured.channel}</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">{featured.title}</h2>
            {featured.logline && <p className="text-white/70 mt-2 line-clamp-2">{featured.logline}</p>}
            <div className="mt-4">
              <span className="kinetic-btn rounded-sm px-6 py-3 inline-flex items-center gap-2 font-bold"><Play className="w-4 h-4" /> Watch now</span>
            </div>
          </div>
        </Link>
      )}

      {items.length > 0 && (
        <section>
          <div className="mono text-white/60 mb-4">LIBRARY OF CONTENT</div>
          <div className="flex gap-4 overflow-x-auto pb-3" data-testid="content-gallery">
            {items.map(c => <Poster key={c.id} c={c} />)}
          </div>
        </section>
      )}

      {trending.length > 0 && (
        <section>
          <div className="mono text-white/60 mb-4">TOP TRENDING · MOST WATCHED</div>
          <div className="flex gap-4 overflow-x-auto pb-3" data-testid="trending-row">
            {trending.map((c, i) => (
              <div key={c.id} className="relative shrink-0" data-testid={`trending-${c.id}`}>
                <span className="absolute -left-1 -top-2 z-10 mono text-black bg-kinetic px-1.5 py-0.5 rounded-sm">#{i + 1}</span>
                <Poster c={c} />
                {c.views > 0 && <div className="mono text-white/50 mt-1 text-center">{c.views} VIEWS</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {channels.length > 0 && (
        <section>
          <div className="mono text-white/60 mb-4">LIST OF CHANNELS · FROM OTHER STREAMERS</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 rounded-lg overflow-hidden" data-testid="channels-row">
            {channels.map((ch, i) => (
              <div key={i} className="bg-[#0E0E0E] p-5" data-testid={`channel-${i}`}>
                <div className="font-sans font-bold truncate">{ch.channel}</div>
                <div className="mono text-white/40 mt-1">{ch.count} TITLE{ch.count > 1 ? "S" : ""}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {artists.length > 0 && (
        <section>
          <div className="mono text-white/60 mb-4">LIST OF ARTISTS · CREATORS ON INDIEWOOD</div>
          <div className="flex gap-4 overflow-x-auto pb-3" data-testid="artists-row">
            {artists.map((a, i) => {
              const inner = (
                <>
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-white/10 border border-white/10 mx-auto">
                    {a.picture ? <img src={posterUrl(a.picture)} alt={a.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      : <div className="w-full h-full flex items-center justify-center font-sans font-bold text-white/60">{a.name?.[0] || "A"}</div>}
                  </div>
                  <div className="font-sans font-bold text-sm mt-2 truncate text-center">{a.name}</div>
                  <div className="mono text-white/40 mt-0.5 text-center">{a.titles} TITLE{a.titles > 1 ? "S" : ""}</div>
                </>
              );
              return a.slug
                ? <Link key={i} to={`/u/${a.slug}`} target="_blank" className="shrink-0 w-[140px] bg-[#121212] border border-white/10 rounded-lg p-4 hover:border-kinetic/60 transition" data-testid={`artist-${i}`}>{inner}</Link>
                : <div key={i} className="shrink-0 w-[140px] bg-[#121212] border border-white/10 rounded-lg p-4" data-testid={`artist-${i}`}>{inner}</div>;
            })}
          </div>
        </section>
      )}

      {topSelling.length > 0 && (
        <section>
          <div className="mono text-white/60 mb-4">🔥 TOP SELLING THIS SEASON</div>
          <div className="flex gap-4 overflow-x-auto pb-3" data-testid="top-selling">
            {topSelling.map((c, i) => (
              <div key={c.id} className="relative shrink-0" data-testid={`top-selling-${c.id}`}>
                <span className="absolute -left-1 -top-2 z-10 mono text-black bg-kinetic px-1.5 py-0.5 rounded-sm">#{i + 1}</span>
                <Poster c={c} />
                <div className="mono text-kinetic mt-1 text-center">{c.sales} SOLD</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
